import type { User } from './types';
import type { DownloadAnalytics } from './download-analytics';
import type { PaymentOperationsPage } from './payment-operations';

const ERP_TOKEN_KEY = 'hosty_erp_access_token';

export interface ErpAgent {
  id: number;
  user_id: number;
  email: string;
  name: string;
  surname: string;
  status: 'active' | 'inactive' | 'suspended';
  availability_status: string;
  current_level: 'bronze' | 'silver' | 'gold' | 'diamond';
  professional_role?: 'technician' | 'teamLead';
  total_tickets_resolved: number;
  total_tickets_assigned: number;
  total_sales_made: number;
  total_earnings: string | number;
  average_rating: string | number | null;
  joined_at: string;
  last_activity_at: string | null;
  user_status: string;
  open_tickets?: number;
  referral_code?: string | null;
  tax_id?: string | null;
  billing_name?: string | null;
  billing_address?: string | null;
  pending_earnings_total?: number;
  pending_payout_count?: number;
  pending_payout_amount?: number;
}

export interface ErpPayout {
  id: number;
  agent_id: number;
  agent_name?: string;
  agent_surname?: string;
  agent_email?: string;
  period_start: string;
  period_end: string;
  tickets_resolved: number;
  tickets_earnings: number;
  positive_ratings: number;
  rating_bonus: number;
  sales_commission: number;
  referral_milestone_bonus?: number;
  total_amount: number;
  status: 'pending' | 'processing' | 'paid' | 'cancelled';
  payment_method?: string | null;
  payment_reference?: string | null;
  paid_at?: string | null;
  created_at: string;
}

export interface ErpPendingEarnings {
  tickets: number;
  positive_ratings: number;
  rating_bonus: number;
  sales_commission: number;
  referral_milestone_bonus?: number;
  total: number;
}

export interface ErpOverview {
  agents: { total: number; active: number; inactive: number; suspended: number };
  tickets: { total: number; pending: number; active_tickets: number; resolved_month: number };
  sales: { total_month: number; commission_month: number };
  payouts_pending: number;
  payouts_pending_amount: number;
  earnings_pending_total: number;
  agents_with_pending: number;
}

export type ErpUserStatus =
  | 'activa'
  | 'caducada'
  | 'cancelada'
  | 'desactivada'
  | 'pending_verification';

export interface ErpUsersOverview {
  total: number;
  verified: number;
  unverified: number;
  with_mfa: number;
  new_today: number;
  new_week: number;
  new_month: number;
  with_license: number;
  with_active_license: number;
  by_status: Record<ErpUserStatus, number>;
}

export interface ErpUserRow {
  id: number;
  email: string;
  name: string;
  surname: string;
  status: ErpUserStatus;
  mfa_enabled: boolean;
  created_at: string;
  license_count: number;
  active_license_count: number;
}

export interface ErpPagination {
  page: number;
  limit: number;
  total: number;
  total_pages: number;
}

export function getErpToken(): string | null {
  return localStorage.getItem(ERP_TOKEN_KEY);
}

export function setErpToken(token: string | null): void {
  if (token) {
    localStorage.setItem(ERP_TOKEN_KEY, token);
  } else {
    localStorage.removeItem(ERP_TOKEN_KEY);
  }
}

async function erpFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getErpToken();
  const headers = new Headers(options.headers);
  if (!headers.has('Content-Type') && options.body) {
    headers.set('Content-Type', 'application/json');
  }
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const response = await fetch(path, { ...options, headers, credentials: 'include' });
  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(
      (data as { error?: string; message?: string }).error ??
        (data as { message?: string }).message ??
        `Error ${response.status}`
    );
  }

  return data as T;
}

export async function erpLogin(email: string, password: string) {
  const result = await erpFetch<{ accessToken: string; user: User }>('/api/account/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });

  if (result.user.role !== 'admin') {
    throw new Error('Solo usuarios administradores pueden acceder al ERP');
  }

  setErpToken(result.accessToken);
  return result;
}

export async function erpLogout() {
  try {
    const token = getErpToken();
    if (token) {
      await erpFetch('/api/account/logout', { method: 'POST' });
    }
  } catch {
    // ignore
  } finally {
    setErpToken(null);
  }
}

export async function erpGetMe(): Promise<User> {
  return erpFetch<User>('/api/account/me');
}

export interface ErpInstallRow {
  channel: string;
  app_version: string;
  installs: number;
  active_30d: number;
}

export interface ErpInstallDetail {
  pos_name: string | null;
  license_id: number | null;
  channel: string;
  app_version: string;
  last_seen: string;
}

export type ErpVerifactuApprovalStatus = 'pending' | 'approved' | 'rejected';

export interface ErpVerifactuClient {
  id: number;
  license_id: number;
  user_id: number;
  customer_name: string;
  customer_email: string;
  account_role: 'admin' | 'employee' | 'user';
  is_demo: boolean;
  license_name: string;
  license_status: string;
  module_enabled: boolean;
  nif: string;
  business_name: string;
  entorno: 'test' | 'prod';
  representacion: 'Inexistente' | 'Correcto' | 'Rechazado' | null;
  admin_status: ErpVerifactuApprovalStatus;
  admin_note: string | null;
  approved_at: string | null;
  remote_active: boolean | null;
  remote_error: string | null;
  last_checked_at: string | null;
  records_total: number;
  records_ok: number;
  records_pending: number;
  records_error: number;
  updated_at: string;
}

export interface ErpVerifactuSummary {
  total: number;
  pending: number;
  approved: number;
  test: number;
  live: number;
  with_error: number;
}

export const erpApi = {
  getDownloads(days: number, product: string) {
    const params = new URLSearchParams({ days: String(days), product });
    return erpFetch<DownloadAnalytics>(`/api/admin/telemetry/downloads?${params}`);
  },
  getPaymentOperations(query: { month: string; page: number; queuePage: number; licenseId?: number }) {
    const params = new URLSearchParams({ month: query.month, page: String(query.page), queuePage: String(query.queuePage) });
    if (query.licenseId !== undefined) params.set('licenseId', String(query.licenseId));
    return erpFetch<PaymentOperationsPage>(`/api/admin/payment-operations?${params}`);
  },

  refreshPaymentOperation(channel: 'menu' | 'terminal', id: string) {
    return erpFetch<{ state: string; reason: string | null }>(
      `/api/admin/payment-operations/${channel}/${encodeURIComponent(id)}/refresh`, { method: 'POST' });
  },

  getOverview() {
    return erpFetch<{ overview: ErpOverview }>('/api/admin/overview');
  },

  getInstallTelemetry() {
    return erpFetch<{ distribution: ErpInstallRow[]; installs: ErpInstallDetail[] }>(
      '/api/admin/telemetry/installs'
    );
  },

  listVerifactu(params: { search?: string; entorno?: string; status?: string } = {}) {
    const query = new URLSearchParams();
    if (params.search) query.set('search', params.search);
    if (params.entorno) query.set('entorno', params.entorno);
    if (params.status) query.set('status', params.status);
    const qs = query.toString();
    return erpFetch<{
      clients: ErpVerifactuClient[];
      summary: ErpVerifactuSummary;
      configured: boolean;
    }>(`/api/admin/verifactu${qs ? `?${qs}` : ''}`);
  },

  refreshVerifactu(nifId: number) {
    return erpFetch<{ client: ErpVerifactuClient; message: string }>(
      `/api/admin/verifactu/${nifId}/refresh`,
      { method: 'POST', body: '{}' }
    );
  },

  setVerifactuApproval(
    nifId: number,
    status: ErpVerifactuApprovalStatus,
    note?: string
  ) {
    return erpFetch<{ client: ErpVerifactuClient; message: string }>(
      `/api/admin/verifactu/${nifId}/approval`,
      { method: 'PATCH', body: JSON.stringify({ status, note }) }
    );
  },

  promoteVerifactuLive(nifId: number) {
    return erpFetch<{ client: ErpVerifactuClient; message: string }>(
      `/api/admin/verifactu/${nifId}/promote-live`,
      { method: 'POST', body: '{}' }
    );
  },

  listAgents() {
    return erpFetch<{ agents: ErpAgent[] }>('/api/admin/agents');
  },

  changePassword(currentPassword: string, newPassword: string) {
    return erpFetch<{ message: string }>('/api/account/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  },

  getUsersOverview() {
    return erpFetch<{ overview: ErpUsersOverview }>('/api/admin/users/overview');
  },

  listUsers(params: { search?: string; status?: string; page?: number; limit?: number } = {}) {
    const query = new URLSearchParams();
    if (params.search) query.set('search', params.search);
    if (params.status) query.set('status', params.status);
    if (params.page) query.set('page', String(params.page));
    if (params.limit) query.set('limit', String(params.limit));
    const qs = query.toString();
    return erpFetch<{ users: ErpUserRow[]; pagination: ErpPagination }>(
      `/api/admin/users${qs ? `?${qs}` : ''}`
    );
  },

  getAgent(agentId: number) {
    return erpFetch<{
      agent: ErpAgent;
      recentTickets: Array<Record<string, unknown>>;
      recentSales: Array<Record<string, unknown>>;
      referralCodes: Array<Record<string, unknown>>;
      payouts: ErpPayout[];
      pendingEarnings: ErpPendingEarnings | null;
    }>(`/api/admin/agents/${agentId}`);
  },

  getPendingEarnings(agentId: number) {
    return erpFetch<{
      pending: ErpPendingEarnings;
      pendingSales: Array<Record<string, unknown>>;
      verifiedSales: Array<Record<string, unknown>>;
      unpaidTickets: Array<Record<string, unknown>>;
    }>(`/api/admin/agents/${agentId}/pending-earnings`);
  },

  listPayouts(status?: string) {
    const query = status ? `?status=${status}` : '';
    return erpFetch<{ payouts: ErpPayout[] }>(`/api/admin/payouts${query}`);
  },

  createPayout(agentId: number, adminNotes?: string) {
    return erpFetch<{ payout: ErpPayout; message: string }>(`/api/admin/agents/${agentId}/payouts`, {
      method: 'POST',
      body: JSON.stringify({ admin_notes: adminNotes }),
    });
  },

  verifySale(saleId: number) {
    return erpFetch<{ message: string }>(`/api/admin/sales/${saleId}/verify`, {
      method: 'PATCH',
      body: '{}',
    });
  },

  markPayoutPaid(
    payoutId: number,
    data: { payment_reference?: string; payment_method?: string; admin_notes?: string }
  ) {
    return erpFetch<{ payout: ErpPayout; message: string }>(`/api/admin/payouts/${payoutId}/pay`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  createAgent(data: { email: string; name: string; surname: string; current_level?: string }) {
    return erpFetch<{
      agent: ErpAgent & { referral_code: string };
      message: string;
      provisional_password: string;
      email_sent: boolean;
      email_error?: string;
    }>('/api/admin/agents', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  createUser(data: {
    email: string;
    name: string;
    surname: string;
    representationEntityType: 'fisica' | 'juridica';
    representativeNif: string;
  }) {
    return erpFetch<{
      message: string;
      user: { id: number; email: string; name: string; surname: string };
      provisional_password: string;
      email_sent: boolean;
      email_error?: string;
    }>('/api/admin/users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updateAgent(
    agentId: number,
    data: {
      status?: string;
      current_level?: string;
      professional_role?: string;
      availability_status?: string;
      tax_id?: string | null;
      billing_name?: string | null;
      billing_address?: string | null;
      reset_password?: string;
    }
  ) {
    return erpFetch<{ message: string }>(`/api/admin/agents/${agentId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },
};
